/**
 * Program library.
 *
 * Each entry says what it is and what it's for. Frequency and timing are
 * derived from the cycles themselves and shown on the card, so this file only
 * carries what the numbers can't: the purpose, where the parameters came from,
 * and a practical note where one genuinely applies.
 *
 * Parameter sources:
 *   - FDA-approved bone growth stimulator protocols (15 Hz gate + kHz carrier)
 *   - Published trial parameters (research/04-pemf-clinical-evidence.md)
 *   - Sedona Pro user manual, 36 programs
 *   - FlexPulse G2 manual, 10 programs with per-program Hz
 *   - Qi Coil published protocols
 *   - Nogier auriculotherapy series
 */

import { RECOMMENDED_CARRIER_HZ } from '../core/limits';
import type { Category, CycleSettings, Program } from '../core/types';

const base: CycleSettings = {
  onMin: 20,
  offMin: 0,
  carrierHz: RECOMMENDED_CARRIER_HZ,
  intensity: 70,
  sweepEnabled: false,
  gateHz: 10,
  sweepLo: 8,
  sweepHi: 12,
  sweepHold: 10,
  sweepTrue: true,
  gateDutyPct: 50,
};

type Spec = {
  id: string;
  name: string;
  title: string;
  category: Category;
  purpose: string;
  origin?: string;
  note?: string;
  gate?: number;
  sweep?: { lo: number; hi: number; hold?: number; bounce?: boolean };
  cycles?: CycleSettings[];
  min?: number;
  intensity?: number;
};

function build(s: Spec): Program {
  const cycles: CycleSettings[] =
    s.cycles ??
    [
      {
        ...base,
        onMin: s.min ?? 20,
        intensity: s.intensity ?? 70,
        ...(s.sweep
          ? {
              sweepEnabled: true,
              sweepLo: s.sweep.lo,
              sweepHi: s.sweep.hi,
              sweepHold: s.sweep.hold ?? 10,
              sweepTrue: s.sweep.bounce ?? true,
            }
          : { sweepEnabled: false, gateHz: s.gate ?? 10 }),
      },
    ];

  return {
    id: s.id,
    name: s.name,
    title: s.title,
    category: s.category,
    cycles,
    createdAt: 0,
    updatedAt: 0,
    info: { purpose: s.purpose, origin: s.origin, note: s.note },
  };
}

const SPECS: Spec[] = [
  /* ---------- Repair & recovery ---------- */
  {
    id: 'bone-heal',
    name: 'BONE_HEAL',
    title: 'Bone healing',
    category: 'evidence',
    gate: 15,
    min: 30,
    intensity: 80,
    purpose: 'Bone healing, particularly fractures that have been slow to knit.',
    origin:
      'The 15 Hz gate used by FDA-approved bone growth stimulators, where it is applied for several hours a day over months.',
    note: 'Cleared devices pair 15 Hz with a ~3.85 kHz carrier. This coil cannot follow a carrier that fast, so the carrier here is set to something the hardware can actually produce — the 15 Hz gate is exact.',
  },
  {
    id: 'joint-pain',
    name: 'JOINT_PAIN',
    title: 'Joint pain',
    category: 'evidence',
    gate: 10,
    min: 25,
    purpose: 'Aching or stiff joints, most often knees.',
    origin: 'The frequency band used across most osteoarthritis PEMF trials.',
  },
  {
    id: 'tissue',
    name: 'TISSUE',
    title: 'Wound & tissue repair',
    category: 'evidence',
    gate: 27,
    min: 20,
    intensity: 60,
    purpose: 'Skin and soft-tissue healing, including slow-healing wounds.',
    origin: 'Parameters from wound-healing and post-surgical trials at comparable field strength.',
  },
  {
    id: 'inflammation',
    name: 'INFLAM',
    title: 'Inflammation',
    category: 'evidence',
    gate: 15,
    min: 25,
    purpose: 'General inflammation and swelling.',
    origin:
      'Works through the calcium-calmodulin / nitric oxide pathway, the mechanism most often cited for PEMF anti-inflammatory effects.',
  },
  {
    id: 'injury',
    name: 'INJURY',
    title: 'Soft-tissue injury',
    category: 'evidence',
    gate: 15,
    min: 25,
    purpose: 'Sprains, strains and pulled muscles.',
    origin: 'Shares parameters with the wound-repair protocols.',
  },
  {
    id: 'swelling',
    name: 'SWELLING',
    title: 'Swelling & edema',
    category: 'evidence',
    gate: 27,
    min: 20,
    intensity: 60,
    purpose: 'Fluid retention and post-injury or post-surgical swelling.',
    origin: 'Edema is one of the two indications PEMF-adjacent devices hold FDA clearance for.',
  },
  {
    id: 'arthritis-15',
    name: 'ARTHRITIS',
    title: 'Arthritis',
    category: 'evidence',
    gate: 15,
    min: 30,
    purpose: 'Longer-running joint pain and stiffness.',
    origin: 'A widely circulated 15 Hz arthritis protocol.',
  },

  /* ---------- Pain ---------- */
  {
    id: 'back',
    name: 'BACK',
    title: 'Lower back',
    category: 'pain',
    gate: 10,
    min: 25,
    purpose: 'Lower back ache and stiffness.',
  },
  {
    id: 'neck',
    name: 'NECK',
    title: 'Neck & shoulders',
    category: 'pain',
    gate: 10,
    min: 20,
    purpose: 'Tension across the shoulders and base of the neck.',
  },
  {
    id: 'muscle',
    name: 'MUSCLE',
    title: 'Large muscle groups',
    category: 'pain',
    cycles: [
      { ...base, gateHz: 10, onMin: 10, offMin: 0, intensity: 70 },
      { ...base, gateHz: 100, onMin: 10, offMin: 0, intensity: 70 },
    ],
    purpose: 'Thighs, glutes, larger muscle groups after exertion.',
    origin: 'FlexPulse G2 programme P9, which alternates 10 Hz and 100 Hz.',
  },
  {
    id: 'nerve',
    name: 'NERVE',
    title: 'Nerve discomfort',
    category: 'pain',
    gate: 5,
    min: 25,
    purpose: 'Tingling, burning or numbness in hands and feet.',
  },
  {
    id: 'fibro',
    name: 'FIBRO',
    title: 'Widespread pain',
    category: 'pain',
    gate: 7.83,
    min: 30,
    intensity: 50,
    purpose: 'Diffuse body-wide aching, run gently and longer.',
  },
  {
    id: 'bruise',
    name: 'BRUISE',
    title: 'Bruising',
    category: 'pain',
    gate: 27,
    min: 15,
    intensity: 60,
    purpose: 'Fresh bruises and knocks.',
  },

  /* ---------- Sleep & calm ---------- */
  {
    id: 'sleep-deep',
    name: 'SLEEP_DEEP',
    title: 'Deep sleep',
    category: 'sleep',
    gate: 3,
    min: 30,
    intensity: 50,
    purpose: 'Staying asleep and reaching deeper sleep.',
    origin: 'Delta band. FlexPulse G2 programme P1.',
  },
  {
    id: 'sleep-onset',
    name: 'SLEEP_DOWN',
    title: 'Wind-down ramp',
    category: 'sleep',
    cycles: [
      { ...base, gateHz: 8, onMin: 6, offMin: 0, intensity: 50 },
      { ...base, gateHz: 6, onMin: 6, offMin: 0, intensity: 50 },
      { ...base, gateHz: 4, onMin: 6, offMin: 0, intensity: 45 },
      { ...base, gateHz: 2, onMin: 8, offMin: 0, intensity: 40 },
    ],
    purpose: 'Falling asleep — steps down from alpha to delta over 26 minutes.',
  },
  {
    id: 'schumann',
    name: 'SCHUMANN',
    title: 'Schumann 7.83 Hz',
    category: 'sleep',
    gate: 7.83,
    min: 20,
    intensity: 50,
    purpose: 'General calm and grounding.',
    origin:
      'The measured fundamental of the Schumann resonance, the Earth-ionosphere cavity frequency.',
  },
  {
    id: 'calm',
    name: 'CALM',
    title: 'Calm — alpha sweep',
    category: 'sleep',
    sweep: { lo: 8, hi: 12, hold: 20 },
    min: 20,
    intensity: 50,
    purpose: 'Settling down without going all the way to sleep.',
    origin: 'Alpha band, 20 seconds at each 1 Hz step.',
  },
  {
    id: 'relax-deep',
    name: 'RELAX',
    title: 'Deep relaxation',
    category: 'sleep',
    sweep: { lo: 1, hi: 5, hold: 30 },
    min: 30,
    intensity: 40,
    purpose: 'Slow, low and gentle — for winding right down.',
    origin: 'Sedona Pro RELAX set, 1–5 Hz at low intensity.',
  },
  {
    id: 'stress',
    name: 'STRESS',
    title: 'Stress wind-down',
    category: 'sleep',
    gate: 7.83,
    min: 15,
    intensity: 45,
    purpose: 'A short reset in the middle of a difficult day.',
  },
  {
    id: 'night-long',
    name: 'NIGHT',
    title: 'Overnight low & slow',
    category: 'sleep',
    cycles: [
      { ...base, gateHz: 3, onMin: 20, offMin: 100, intensity: 35 },
      { ...base, gateHz: 3, onMin: 20, offMin: 100, intensity: 35 },
      { ...base, gateHz: 3, onMin: 20, offMin: 0, intensity: 35 },
    ],
    purpose: 'Three short blocks spread across the night rather than one long run.',
    origin:
      'Follows the intermittent dosing pattern that outperformed continuous exposure at matched dose in cell studies.',
  },

  /* ---------- Energy & focus ---------- */
  {
    id: 'energy',
    name: 'ENERGY',
    title: 'Morning energy',
    category: 'energy',
    sweep: { lo: 10, hi: 20, hold: 15 },
    min: 15,
    purpose: 'Waking up and getting going.',
    origin: 'Ascending sweep from alpha into low beta, 15 seconds per step.',
  },
  {
    id: 'alert',
    name: 'ALERT',
    title: 'Alertness',
    category: 'energy',
    gate: 23,
    min: 15,
    purpose: 'Feeling sharper when tired.',
    origin: 'Beta band. FlexPulse G2 programme P5.',
  },
  {
    id: 'focus',
    name: 'FOCUS',
    title: 'Focus',
    category: 'energy',
    gate: 14,
    min: 20,
    purpose: 'Concentration and sustained attention.',
    origin: 'Sensorimotor rhythm band. FlexPulse G2 programme P4.',
  },
  {
    id: 'vitality',
    name: 'VITALITY',
    title: 'Vitality',
    category: 'energy',
    gate: 25,
    min: 20,
    purpose: 'General pick-up.',
    origin: 'Sedona Pro WELLNESS set.',
  },
  {
    id: 'recovery',
    name: 'RECOVERY',
    title: 'Post-exercise recovery',
    category: 'energy',
    sweep: { lo: 8, hi: 70, hold: 5 },
    min: 25,
    purpose: 'After training or a long day on your feet.',
    origin: 'Sedona Pro SPORT set — a wide 8–70 Hz sweep, 5 seconds per step.',
  },
  {
    id: 'stamina',
    name: 'STAMINA',
    title: 'Endurance',
    category: 'energy',
    gate: 73,
    min: 15,
    purpose: 'Stamina and staying power.',
    origin: 'Sedona Pro SPORT set. Also Nogier G.',
  },

  /* ---------- Immune & allergy ---------- */
  {
    id: 'allergy',
    name: 'ALLERGY',
    title: 'Allergy support',
    category: 'immune',
    gate: 15,
    min: 25,
    purpose: 'Allergic reactions and the inflammation that comes with them.',
    origin: 'Uses the anti-inflammatory parameters rather than a frequency specific to allergy.',
  },
  {
    id: 'histamine',
    name: 'HISTAMINE',
    title: 'Histamine intolerance',
    category: 'immune',
    gate: 15,
    min: 25,
    intensity: 60,
    purpose: 'Flushing, headache and gut symptoms after high-histamine foods.',
    origin: 'Gentle anti-inflammatory parameters.',
  },
  {
    id: 'immune',
    name: 'IMMUNE',
    title: 'General immune support',
    category: 'immune',
    sweep: { lo: 1, hi: 10, hold: 30 },
    min: 25,
    intensity: 60,
    purpose: 'Run when you feel something coming on.',
    origin: 'Sedona Pro WELLNESS set, 1–10 Hz at 30 seconds per step.',
  },
  {
    id: 'sinus',
    name: 'SINUS',
    title: 'Sinus & congestion',
    category: 'immune',
    gate: 10,
    min: 15,
    intensity: 50,
    purpose: 'Blocked sinuses and head congestion.',
    note: 'Often used on the upper chest rather than the face.',
  },
  {
    id: 'seasonal',
    name: 'SEASONAL',
    title: 'Seasonal symptoms',
    category: 'immune',
    gate: 10,
    min: 20,
    intensity: 60,
    purpose: 'Hay fever season and seasonal flare-ups.',
  },

  /* ---------- Digestion & metabolism ---------- */
  {
    id: 'liver',
    name: 'LIVER',
    title: 'Liver support',
    category: 'digestive',
    gate: 10,
    min: 20,
    intensity: 60,
    purpose: 'Liver function and general abdominal support.',
    note: 'One animal study (Li 2015) found liver changes in rats at 50 Hz and 10–20 mT sustained over 10 weeks — roughly 10–20x this device at depth. Worth knowing if you plan to run this daily over months.',
  },
  {
    id: 'digest',
    name: 'DIGEST',
    title: 'Digestive comfort',
    category: 'digestive',
    gate: 10,
    min: 20,
    intensity: 55,
    purpose: 'Bloating and general digestive discomfort.',
  },
  {
    id: 'gut',
    name: 'GUT',
    title: 'Gut calm',
    category: 'digestive',
    sweep: { lo: 1, hi: 5, hold: 30 },
    min: 25,
    intensity: 50,
    purpose: 'Cramping and an unsettled gut. Slow and gentle.',
  },
  {
    id: 'metabolic',
    name: 'METABOLIC',
    title: 'Metabolic support',
    category: 'digestive',
    gate: 10,
    min: 25,
    purpose: 'General metabolic and abdominal session.',
  },
  {
    id: 'detox',
    name: 'SETTLE',
    title: 'Post-session settle',
    category: 'digestive',
    sweep: { lo: 1, hi: 10, hold: 20 },
    min: 20,
    intensity: 45,
    purpose: 'A gentle low-frequency run to follow a stronger session.',
  },

  /* ---------- Circulation ---------- */
  {
    id: 'circulation',
    name: 'CIRC',
    title: 'Circulation',
    category: 'circulation',
    gate: 10,
    min: 20,
    purpose: 'Blood flow, particularly to hands and feet.',
  },
  {
    id: 'lymph',
    name: 'LYMPH',
    title: 'Lymphatic',
    category: 'circulation',
    sweep: { lo: 1, hi: 10, hold: 20 },
    min: 25,
    intensity: 55,
    purpose: 'Lymph movement and fluid drainage.',
  },
  {
    id: 'cold-limbs',
    name: 'WARMTH',
    title: 'Cold hands & feet',
    category: 'circulation',
    gate: 10,
    min: 15,
    purpose: 'Extremities that stay cold.',
  },
  {
    id: 'micro',
    name: 'MICRO',
    title: 'Microcirculation',
    category: 'circulation',
    gate: 30,
    min: 20,
    intensity: 60,
    purpose: 'Small-vessel blood flow.',
    origin: 'Sedona Pro WELLNESS set, 30 Hz impulse.',
  },

  /* ---------- Head, eyes & teeth ---------- */
  {
    id: 'brain-alpha',
    name: 'BRAIN_A',
    title: 'Brain — alpha',
    category: 'head',
    gate: 10,
    min: 15,
    intensity: 40,
    purpose: 'Mental calm and a settled head.',
    origin: 'Alpha band. FlexPulse G2 programme P3.',
  },
  {
    id: 'gamma-40',
    name: 'GAMMA_40',
    title: 'Brain — gamma 40 Hz',
    category: 'head',
    gate: 40,
    min: 20,
    intensity: 40,
    purpose: 'Cognition and mental sharpness.',
    origin:
      'FlexPulse G2 programme P6. The widely cited 40 Hz cognition research used light and sound rather than magnetic fields.',
  },
  {
    id: 'clarity',
    name: 'CLARITY',
    title: 'Mental clarity',
    category: 'head',
    gate: 14,
    min: 15,
    intensity: 40,
    purpose: 'Clearing brain fog.',
    origin: 'Sensorimotor rhythm band, applied at the head.',
  },
  {
    id: 'eyes',
    name: 'EYES',
    title: 'Eye strain',
    category: 'head',
    gate: 10,
    min: 10,
    intensity: 30,
    purpose: 'Tired eyes after screens or close work.',
    note: 'Short and low-intensity on purpose. The retina responds to magnetic fields more readily than any other tissue — flickering visual sensations are the first thing people notice near the head, most easily around 20 Hz.',
  },
  {
    id: 'teeth',
    name: 'TEETH',
    title: 'Teeth & gums',
    category: 'head',
    gate: 15,
    min: 12,
    intensity: 40,
    purpose: 'Dental discomfort and gum soreness.',
    origin: 'Borrows the bone-healing frequency, on the reasoning that teeth sit in bone.',
    note: 'Metal dental work — amalgam, crowns, implants — will interact with a pulsed magnetic field and may warm slightly or feel odd. Stop if you notice anything.',
  },
  {
    id: 'jaw',
    name: 'JAW',
    title: 'Jaw tension',
    category: 'head',
    gate: 10,
    min: 12,
    intensity: 40,
    purpose: 'Clenching and jaw ache.',
  },

  /* ---------- Nogier series ---------- */
  ...([
    ['A', 1.14, 'cellular vitality and general regeneration'],
    ['B', 2.28, 'nutrition and metabolism'],
    ['C', 4.56, 'movement and circulation'],
    ['D', 9.12, 'scars and laterality'],
    ['E', 18.25, 'nerve and spinal tissue'],
    ['F', 36.5, 'subcortical and emotional balance'],
    ['G', 73, 'cortical and muscular tissue'],
  ] as const).map(
    ([letter, hz, use]): Spec => ({
      id: `nogier-${letter.toLowerCase()}`,
      name: `NOGIER_${letter}`,
      title: `Nogier ${letter}`,
      category: 'traditional',
      gate: hz,
      min: 20,
      intensity: 50,
      purpose: `In the Nogier system, associated with ${use}.`,
      origin: `Frequency ${letter} of the seven-tone auriculotherapy series. The whole series is one doubling sequence: f = 1.140625 x 2^n.`,
    }),
  ),
];

export const SEED_PROGRAMS: Program[] = SPECS.map(build);

/* ------------------------------------------------------------------ *
 * Structural presets — shapes, not programmes
 * ------------------------------------------------------------------ */

export const PRESETS: { id: string; label: string; description: string; cycles: CycleSettings[] }[] =
  [
    {
      id: 'short-single',
      label: 'Single session (20 min)',
      description: 'One block. Matches the 8–30 minute range used by commercial systems.',
      cycles: [{ ...base, onMin: 20 }],
    },
    {
      id: 'intermittent',
      label: 'Intermittent (3 x 10 min)',
      description:
        'Short blocks with rest between. Outperformed continuous exposure at matched daily dose in cell studies.',
      cycles: [
        { ...base, onMin: 10, offMin: 120 },
        { ...base, onMin: 10, offMin: 120 },
        { ...base, onMin: 10, offMin: 0 },
      ],
    },
    {
      id: 'overnight-original',
      label: 'Overnight (4 x 30 min, 6.5 h)',
      description:
        'The original device protocol. Considerably longer than any published commercial session.',
      cycles: [
        { ...base, onMin: 30, offMin: 90, intensity: 100 },
        { ...base, onMin: 30, offMin: 90, intensity: 90 },
        { ...base, onMin: 30, offMin: 90, intensity: 90 },
        { ...base, onMin: 30, offMin: 0, intensity: 75 },
      ],
    },
  ];

/* ------------------------------------------------------------------ *
 * Display helpers
 * ------------------------------------------------------------------ */

/** "10 Hz" · "8–12 Hz sweep, 20s/step" · "4 steps: 8 → 2 Hz" */
export function describeFrequency(cycles: CycleSettings[]): string {
  if (cycles.length === 0) return '—';
  const first = cycles[0];

  if (cycles.length > 1) {
    const gates = cycles.filter((c) => !c.sweepEnabled).map((c) => c.gateHz);
    if (gates.length === cycles.length) {
      const unique = [...new Set(gates)];
      if (unique.length === 1) return `${unique[0]} Hz`;
      return `${cycles.length} steps: ${unique[0]} → ${unique[unique.length - 1]} Hz`;
    }
    return `${cycles.length} stages`;
  }

  if (first.sweepEnabled) {
    return `${first.sweepLo}–${first.sweepHi} Hz sweep, ${first.sweepHold}s per step`;
  }
  return `${first.gateHz} Hz`;
}

/** "20 min" · "3 × 20 min over 5h" */
export function describeTiming(cycles: CycleSettings[]): string {
  if (cycles.length === 1) return `${cycles[0].onMin} min`;
  const allSame = cycles.every((c) => c.onMin === cycles[0].onMin);
  const total = cycles.reduce(
    (sum, c, i) => sum + c.onMin + (i < cycles.length - 1 ? c.offMin : 0),
    0,
  );
  const spread = total >= 60 ? ` over ${Math.round((total / 60) * 10) / 10}h` : '';
  return allSame
    ? `${cycles.length} × ${cycles[0].onMin} min${spread}`
    : `${cycles.length} blocks${spread}`;
}
